import { InlineOption } from "@/Common/Components/Inputs/InlineOptionsControl";
import { Locale } from "@/Common/Models/Localization/Locale";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { HeightUnit, WeightUnit } from "@/Common/Utilities/Units/Units";
import { WeightUnitConverter } from "@/Common/Utilities/Units/WeightUnitConverter";
import { BaseBritishColumbiaObstetricalFileSubSection, BaseBritishColumbiaObstetricalFileSubSectionProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/BritishColumbia/Subcomponent/BaseBritishColumbiaObstetricalFileSubSection";
import { BloodPressureField } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/BloodPressureField";
import { ObstetricalFileControlRenderer } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRenderer";
import { DateSelectorRenderOptions, TextboxRenderOptions } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRendererOptions";
import { MedicalCalculator } from "@/Patient/Helpers/MedicalCalculator";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { BritishColumbiaObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/BritishColumbia/BritishColumbiaObstetricalFileForm";
import { BritishColumbiaObstetricalFileInitialPhysicalExamination } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part1/BritishColumbiaObstetricalFileInitialPhysicalExamination";
import { ObstetricalExamResultData } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part1/ObstetricalExamResultData";
import { ObstetricalExamResultState } from "@/Patient/Models/ObstetricalFile/ObstetricalExamResultState";
import { ObstetricalFileVitalSign } from "@/Patient/Models/ObstetricalFile/ObstetricalFileVitalSign";
import { BritishColumbiaObstetricalFilePart1State } from "../BritishColumbiaObstetricalFilePart1";

interface BritishColumbiaObstetricalFileSection10Props extends BaseBritishColumbiaObstetricalFileSubSectionProps {
    vitalSign: ObstetricalFileVitalSign;
    onVitalSignUpdated: () => void;
    getHeightDropdownOptions: (unit?: HeightUnit, isInCentimeter?: boolean) => SearchableItemModelT<string>[];
    mutateVitalSignAndForm: (mutation: (state: BritishColumbiaObstetricalFilePart1State) => void, triggerVitalSignUpdate?: boolean) => void;
}

class BritishColumbiaObstetricalFileSection10 extends BaseBritishColumbiaObstetricalFileSubSection<BritishColumbiaObstetricalFileSection10Props, never> {
    private _vitalSignControlRenderer: ObstetricalFileControlRenderer;
    private readonly otherSkinDescriptionFieldId = "OtherSkinDescription";

    constructor(props: BritishColumbiaObstetricalFileSection10Props) {
        super(props);
        this.setVitalSignControlRenderer(this.props.readonly);
    }

    protected get vitalSignControlRenderer(): ObstetricalFileControlRenderer {
        if (this._vitalSignControlRenderer?.getIsReadonly() !== this.props.readonly) {
            this.setVitalSignControlRenderer(this.props.readonly);
        }
        return this._vitalSignControlRenderer;
    }

    public render(): JSX.Element {
        const section10 = this.props.form.Section10;

        if (!this.props.vitalSign)
            return null;

        const isLatestMedicalItemInEmrTemplate = this.props.obstetricalFileMedicalSummary.isLatestMedicalItemInEmrTemplate(this.props.vitalSign);

        const vitalSignsFieldStyles: React.CSSProperties = !this.props.readonly
            ? { maxWidth: "80px", minWidth: "70px" }
            : {};

        return (
            <div className="section bc-antenatal-section-part10">
                <div>
                    <div className="flex-display flex-col-gap-sm margin-sm-bottom" style={{ justifyContent: "stretch" }}>
                        {this.renderTitle(10, "InitialPhysicalExaminationLabel", "", { alignSelf: "center", margin: 0 })}
                        <div className="field-and-label-container">
                            <label>{this.getString("DateLabel")}</label>
                            <div className="margin-sm-left">
                                {this.controlRenderer.renderDateSelector({
                                    value: section10.ExaminationDate,
                                    onChange: value => this.mutateModelState(s => s.Section10.ExaminationDate = value),
                                    width: 130
                                })}
                            </div>
                        </div>
                        <div className="field-and-label-container">
                            <label>{this.getString("CompletedByLabel")}</label>
                            {this.controlRenderer.renderTextbox({
                                value: section10.CompletedBy,
                                onChange: value => this.props.mutateModelState(s => s.Section10.CompletedBy = value),
                                containerClassName: "margin-sm-left",
                                maxLength: this.page1FieldLimits.InitialPhysicalExamination_CompletedBy.MaxChars,
                                className: "padding-xs-left"
                            })}
                        </div>
                    </div>
                </div>
                <div className="examination-vitalsign-container margin-sm-top margin-sm-bottom">
                    <div className="field-and-label-container">
                        <label>{this.getString("BloodPressureAbbrevLabel")}</label>
                        <BloodPressureField
                            cssClassName="margin-sm-left"
                            readonly={this.props.readonly || !isLatestMedicalItemInEmrTemplate}
                            bloodPressure={this.props.vitalSign?.bloodPressure}
                            isInvalid={this.props.vitalSign.emrIsInvalid}
                            onBloodPressureChange={bloodPressure => this.mutateVitalSignAndForm(s => s.vitalSign.bloodPressure = bloodPressure)}
                            onBlur={() => this.props.onVitalSignUpdated()}
                        />
                    </div>
                    <div className="field-and-label-container flex-col-gap-xs">
                        <label>{this.getString("HeartRateAbbrevLabel")}</label>
                        <span className="extra-info-label">({this.getString("PerMinuteAbbrevLabel")})</span>
                        <div style={{ maxWidth: "65px" }}>
                            {this.vitalSignControlRenderer.renderTextbox({
                                isReadOnlyOverride: this.props.readonly || !isLatestMedicalItemInEmrTemplate,
                                value: this.props.vitalSign?.heartRate?.toString() ?? "",
                                className: `${this.props.vitalSign?.emrIsInvalid ? "invalid-vital-sign" : ""}`,
                                type: "number",
                                min: 0,
                                max: 10000,
                                onChange: heartRateString => this.mutateVitalSignAndForm(s => s.vitalSign.heartRate = heartRateString && Number(heartRateString))
                            })}
                        </div>
                    </div>
                    <div className="field-and-label-container">
                        <label>{this.getString("HeightAbbrevLabel")}</label>
                        <div className="unit-container margin-sm-left">
                            <div style={vitalSignsFieldStyles}>
                                {this.vitalSignControlRenderer.renderAutoCompleteTextbox({
                                    value: this.props.vitalSign?.getHeightIn(section10.HeightUnit)?.toString(),
                                    autoCompleteItems: this.props.getHeightDropdownOptions(section10.HeightUnit),
                                    onChange: value => this.mutateVitalSignAndForm(s => s.vitalSign.height = value ? Number(value) : null, true),
                                    onAutoCompleteSelected: value => this.mutateVitalSignAndForm(s => s.vitalSign.height = value ? Number(value) : null, true),
                                    type: "number",
                                    min: 0,
                                    max: 10000,
                                    isReadOnlyOverride: !isLatestMedicalItemInEmrTemplate,
                                    className: `unit-value ${this.props.vitalSign.emrIsInvalid ? "invalid-vital-sign" : ""}`,
                                    disableMatchAutoSorting: true
                                })}
                            </div>
                            {this.controlRenderer.renderHeightUnitSelectInchCentimeter(
                                section10.HeightUnit,
                                value => this.mutateVitalSignAndForm(
                                    s => {
                                        s.vitalSign.heightUnit = value;
                                        s.form.Section10.HeightUnit = value;
                                    },
                                    true,
                                    true
                                ),
                                "unit",
                                Locale.en
                            )}
                        </div>
                    </div>
                    <div className="field-and-label-container">
                        <label>{this.getString("ObstetricalFile_BC_PrePregnancyWeightLabel")}</label>
                        <div className="unit-container margin-sm-left">
                            <span style={vitalSignsFieldStyles}>
                                {this.controlRenderer.renderAutoCompleteTextbox({
                                    value: section10.PrePregnancyWeight?.toString() ?? "",
                                    autoCompleteItems: this.getWeightBeforePregnancyDropdownOptions(section10.WeightUnit),
                                    onChange: value => this.mutateVitalSignAndForm(s => {
                                        s.form.Section10.PrePregnancyWeight = value ? Number(value) : null;
                                        this.refreshBodyMassIndex(s);
                                        this.autoFillCompletedBy(s.form);
                                    }),
                                    onAutoCompleteSelected: value => this.mutateVitalSignAndForm(s => {
                                        s.form.Section10.PrePregnancyWeight = value && !isNaN(value) ? Number(value) : null;
                                        this.refreshBodyMassIndex(s);
                                        this.autoFillCompletedBy(s.form);
                                    }),
                                    type: "number",
                                    min: 0,
                                    max: 10000,
                                    disableMatchAutoSorting: true,
                                    className: "unit-value"
                                })}
                            </span>
                            {this.controlRenderer.renderWeightUnitSelect(
                                section10.WeightUnit,
                                value => this.onPrePregnancyWeightUnitChangeHandler(value),
                                "unit",
                                Locale.en
                            )}
                        </div>
                    </div>
                    <div className="field-and-label-container">
                        <label>{this.getString("PrePregnancyBodyMassIndexShortAbbrevLabel")}</label>
                        <span className="margin-sm-left" style={vitalSignsFieldStyles}>
                            {this.controlRenderer.renderTextbox({
                                value: section10.PrePregnancyBodyMassIndex?.toString() ?? "",
                                type: "number",
                                min: 0,
                                max: 10000,
                                onChange: value => this.mutateModelState(s => s.Section10.PrePregnancyBodyMassIndex = value ? parseFloat(value) : null)
                            })}
                        </span>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        {this.renderNormalAbnormalExaminationSection()}
                    </div>
                </div>
            </div>
        );
    }

    private renderNormalAbnormalExaminationSection(): JSX.Element {
        return (
            <div className="row row-form">
                <div className="col-md-6">
                    {this.renderNormalAbnormalHeaders()}
                    {this.renderExamRadioButtonsWithTextBox(
                        "Examination_HeadAndNeckResult",
                        "ObstetricalFile_BC_HeadAndNeckLabel",
                        section10 => section10.HeadAndNeckExamResult,
                        this.page1FieldLimits.InitialPhysicalExamination_HeadAndNeckDescription.MaxChars

                    )}
                    {this.renderExamRadioButtonsWithTextBox(
                        "Examination_BreastsAndNipplesResult",
                        "BreastsAndNipplesLabel",
                        section10 => section10.BreastAndNipplesExamResult,
                        this.page1FieldLimits.InitialPhysicalExamination_BreastsAndNipplesDescription.MaxChars
                    )}
                    {this.renderExamRadioButtonsWithTextBox(
                        "Examination_HeartAndLungsResult",
                        "HeartAndLungsLabel",
                        section10 => section10.HeartAndLungExamResult,
                        this.page1FieldLimits.InitialPhysicalExamination_HeartAndLungsDescription.MaxChars
                    )}
                    {this.renderExamRadioButtonsWithTextBox(
                        "Examination_AbdomenResult",
                        "AbdomenLabel",
                        section10 => section10.AbdomenExamResult,
                        this.page1FieldLimits.InitialPhysicalExamination_AbdomenDescription.MaxChars
                    )}
                    {this.renderExamRadioButtonsWithTextBox(
                        "Examination_MusculoskeletalResult",
                        "MusculoskeletalLabel",
                        section10 => section10.MusculoskeletalExamResult,
                        this.page1FieldLimits.InitialPhysicalExamination_MusculoskeletalDescription.MaxChars
                    )}
                </div>
                <div className="col-md-6">
                    {this.renderNormalAbnormalHeaders()}
                    {this.renderSkinExamResult()}
                    {this.renderPelvicExamResult()}
                    {this.renderExamRadioButtonsWithTextBox(
                        "Examination_OtherResult",
                        "OtherLabel",
                        section10 => section10.OtherExamResult,
                        this.page1FieldLimits.InitialPhysicalExamination_OtherDescription.MaxChars
                    )}
                </div>
                {!this.props.readonly && (
                    <div className="col-md-12">
                        <a onClick={() => this.mutateModelStateWithFormUpdate(s => s.Section10.setEmptyFieldsAsNormal())}>
                            {this.getString("ObstetricalFile_MarkAllEmptyFieldsAsNormalLabel")}
                        </a>
                    </div>
                )}
            </div>
        );
    }

    private renderSkinExamResult(): JSX.Element {
        const section10 = this.props.form.Section10;
        return (
            <div className="flex-display" style={{ alignItems: "baseline", "gap": "5px" }}>
                {this.renderNormalAbnormalButtonsWithAssociatedLabel(
                    "ExaminationResult_Skin",
                    "SkinLabel",
                    section10.SkinExamResult.Result,
                    value => this.mutateModelStateWithFormUpdate(s => s.Section10.SkinExamResult.setResult(value))
                )}
                <div className="flex-display width-100" style={{ flexDirection: "column", "gap": "10px" }}>
                    {this.controlRenderer.renderCheckbox(
                        "VaricositiesLabel",
                        section10.SkinExamResult.ExtraInfo.HasSkinVaricosities,
                        value => this.mutateModelStateWithFormUpdate(() => {
                            section10.SkinExamResult.ExtraInfo.HasSkinVaricosities = value;
                            section10.SkinExamResult.setAbnormalResultIfNothingSet();
                        })
                    )}
                    <div className="flex-display" style={{ alignItems: "center" }}>
                        {this.controlRenderer.renderCheckbox(
                            "OtherLabel",
                            section10.SkinExamResult.ExtraInfo.HasOtherSkinCondition,
                            value => this.mutateModelStateWithFormUpdate(
                                s => {
                                    s.Section10.SkinExamResult.ExtraInfo.HasOtherSkinCondition = value;
                                    s.Section10.SkinExamResult.setAbnormalResultIfNothingSet();
                                },
                                () => this.setFocusTextbox(this.props.form.Section10.SkinExamResult.ExtraInfo.HasOtherSkinCondition, this.otherSkinDescriptionFieldId)
                            )
                        )}
                        <div className="width-100">
                            {this.getAdditionalTextBox({
                                id: this.otherSkinDescriptionFieldId,
                                value: section10.SkinExamResult.ExtraInfo.OtherSkinConditionDescription,
                                onChange: value => this.mutateModelState(s => {
                                    s.Section10.SkinExamResult.ExtraInfo.OtherSkinConditionDescription = value;
                                    if (s.Section10.SkinExamResult.ExtraInfo.OtherSkinConditionDescription)
                                        s.Section10.SkinExamResult.ExtraInfo.HasOtherSkinCondition = true;

                                    s.Section10.SkinExamResult.setAbnormalResultIfNothingSet();
                                }),
                                valueClassName: "margin-xs-left",
                                maxLength: this.page1FieldLimits.InitialPhysicalExamination_Skin_OtherDescription.MaxChars
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderPelvicExamResult(): JSX.Element {
        const section10 = this.props.form.Section10;

        const renderDateField = (labelKey: string, dateOptions: DateSelectorRenderOptions, textOptions: TextboxRenderOptions): JSX.Element => (
            <div
                className="col-md-12 inline-radio-textbox"
                style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}
            >
                <div className="fit-content padding-sm-right">
                    <label style={{ marginLeft: "68px" }}>{this.getString(labelKey)}</label>
                </div>
                <div className={"flex-display flex-gap-xs " + (this.props.readonly ? "flex-wrap" : "")}>
                    {this.controlRenderer.renderDateSelector({
                        id: dateOptions.id,
                        value: dateOptions.value,
                        onChange: value => dateOptions.onChange(value),
                        width: 120
                    })}
                    {this.controlRenderer.renderTextbox({
                        id: textOptions.id,
                        value: textOptions.value,
                        onChange: value => textOptions.onChange(value),
                        maxLength: 15,
                        className: "min-height-pelvic-text"
                    })}
                </div>
            </div>
        );

        return (
            <div className="row">
                <div className="col-md-12 inline-radio-textbox inline-selection-justify-between">
                    <div className="radio-before-textbox">
                        {this.renderNormalAbnormalButtonsWithAssociatedLabel(
                            "ExaminationResult_Pelvic",
                            "PelvicLabel",
                            section10.PelvicExamResult.Result,
                            value => this.mutateModelStateWithFormUpdate(s => s.Section10.PelvicExamResult.setResult(value))
                        )}
                    </div>
                    <div className="width-100">
                        {this.getAdditionalTextBox({
                            id: "ExaminationResult_Pelvic",
                            value: section10.PelvicExamResult.ExtraInfo.Description,
                            onChange: value => this.mutateModelState(s => {
                                s.Section10.PelvicExamResult.ExtraInfo.Description = value;
                                if (value?.trim())
                                    s.Section10.PelvicExamResult.setAbnormalResultIfNothingSet();
                            }),
                            maxLength: this.page1FieldLimits.InitialPhysicalExamination_PelvicDescription.MaxChars
                        })}
                    </div>
                </div>
                <div>
                    {renderDateField(
                        "SexuallyTransmittedInfectionTestAbbrevLabel",
                        {
                            id: "ExaminationResult_StiTest",
                            value: section10.PelvicExamResult.ExtraInfo.SexuallyTransmittedInfectionExamDate,
                            onChange: value => this.mutateModelStateWithFormUpdate(() => section10.PelvicExamResult.ExtraInfo.SexuallyTransmittedInfectionExamDate = value)
                        },
                        {
                            id: "ExaminationResult_StiTest_textValue",
                            value: section10.PelvicExamResult.ExtraInfo.SexuallyTransmittedInfectionExamValue,
                            onChange: value => this.mutateModelState(() => section10.PelvicExamResult.ExtraInfo.SexuallyTransmittedInfectionExamValue = value)
                        }
                    )}
                </div>
                {renderDateField(
                    "PapTestLabel",
                    {
                        id: "ExaminationResult_PapTestDate",
                        value: section10.PelvicExamResult.ExtraInfo.PapExamCompletionDate,
                        onChange: value => this.mutateModelStateWithFormUpdate(() => section10.PelvicExamResult.ExtraInfo.PapExamCompletionDate = value)
                    },
                    {
                        id: "ExaminationResult_PapTest_textValue",
                        value: section10.PelvicExamResult.ExtraInfo.PapExamResult,
                        onChange: value => this.mutateModelState(() => section10.PelvicExamResult.ExtraInfo.PapExamResult = value)
                    }
                )}
            </div>
        );
    }

    private renderNormalAbnormalButtonsWithAssociatedLabel(
        id: string,
        labelKey: string,
        value: ObstetricalExamResultState,
        onChange: (value: ObstetricalExamResultState) => void
    ): JSX.Element {
        return (
            <div className="flex-display">
                {this.controlRenderer.renderInlineRadio({
                    value: value,
                    items: [
                        new InlineOption<ObstetricalExamResultState>(ObstetricalExamResultState.Normal, ""),
                        new InlineOption<ObstetricalExamResultState>(ObstetricalExamResultState.Abnormal, "")
                    ],
                    onChange: value => {
                        onChange(value);
                        this.setFocusTextbox(value === ObstetricalExamResultState.Abnormal, id);
                    },
                    id: `ExaminationRadiosButton_${labelKey}`,
                    className: "radios-one-label extra-spacing",
                    additionalWrapperClassName: "margin-sm-left",
                    optionSpacing: "20px"
                })}
                <label className="margin-none">{this.getString(labelKey)}</label>
            </div>
        );
    }

    private renderNormalAbnormalHeaders(): JSX.Element {
        return (
            <div className="row">
                <div className="col-md-12">
                    <span className="text-bold margin-sm-right">{this.getString("NormalAbbrevLabel")}</span>
                    <span className="text-bold">{this.getString("AbnormalAbbreviationLabel")}</span>
                </div>
            </div>
        );
    }

    private renderExamRadioButtonsWithTextBox(
        id: string,
        labelKey: string,
        selector: (model: BritishColumbiaObstetricalFileInitialPhysicalExamination) => ObstetricalExamResultData<string>,
        maxLength?: number
    ): JSX.Element {
        const value = selector(this.props.form.Section10);
        if (!value)
            return null;

        return (
            <div className="row">
                <div className="col-md-12 inline-radio-textbox inline-selection-justify-between">
                    <div className="radio-before-textbox">
                        {this.renderNormalAbnormalButtonsWithAssociatedLabel(
                            id,
                            labelKey,
                            value.Result,
                            value => this.mutateModelStateWithFormUpdate(s => selector(s.Section10)?.setResult(value))
                        )}
                    </div>
                    <div className="width-100">
                        {this.getAdditionalTextBox({
                            id: id,
                            value: value.ExtraInfo,
                            onChange: value => this.mutateModelState(s => selector(s.Section10)?.setExtraInfo(value)),
                            maxLength: maxLength
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private mutateModelState(mutation: (state: BritishColumbiaObstetricalFileForm) => void): void {
        this.props.mutateModelState(
            s => {
                mutation(s);
                this.autoFillCompletedBy(s);
            }
        );
    }

    private mutateModelStateWithFormUpdate(mutation: (state: BritishColumbiaObstetricalFileForm) => void, then?: () => void): void {
        this.props.mutateModelStateWithFormUpdate(
            s => {
                mutation(s);
                this.autoFillCompletedBy(s);
            },
            () => then?.()
        );
    }

    private mutateVitalSignAndForm(mutation: (state: BritishColumbiaObstetricalFilePart1State) => void, refreshBodyMassIndex: boolean = false, triggerVitalSignUpdate: boolean = false): void {
        this.props.mutateVitalSignAndForm(
            s => {
                mutation(s);

                if (refreshBodyMassIndex)
                    this.refreshBodyMassIndex(s);

                this.autoFillCompletedBy(s.form);
            },
            triggerVitalSignUpdate
        );
    }

    private autoFillCompletedBy(state: BritishColumbiaObstetricalFileForm): void {
        const completedByBefore = state.Section10.CompletedBy;
        const completeByEdited = completedByBefore !== state.Section10.CompletedBy;

        if (IsNullOrWhiteSpace(state.Section10.CompletedBy) && !completeByEdited)
            state.Section10.CompletedBy = sessionStore.getCurrentEmployee().Name;
    }

    private setVitalSignControlRenderer(readonly: boolean): void {
        this._vitalSignControlRenderer = new ObstetricalFileControlRenderer({
            readonly: readonly,
            onBlur: () => this.props.onVitalSignUpdated()
        });
    }

    private onPrePregnancyWeightUnitChangeHandler(value: WeightUnit): void {
        this.mutateModelStateWithFormUpdate(s => {
            if (s.Section10.PrePregnancyWeight) {
                const convertedPrePregnancyWeight = WeightUnitConverter.convert(s.Section10.WeightUnit, value, s.Section10.PrePregnancyWeight, 1);
                s.Section10.PrePregnancyWeight = convertedPrePregnancyWeight;
            }
            s.Section10.WeightUnit = value;
        });
    }

    private refreshBodyMassIndex(state: BritishColumbiaObstetricalFilePart1State): void {
        let bodyMassIndex: number = null;
        if (state.form.Section10.PrePregnancyWeight && state.vitalSign.height) {
            const weigthInKilograms = this.props.form.Section10.WeightUnit == WeightUnit.Kilogram
                ? this.props.form.Section10.PrePregnancyWeight
                : Math.round(WeightUnitConverter.convert(state.form.Section10.WeightUnit, WeightUnit.Kilogram, state.form.Section10.PrePregnancyWeight));

            bodyMassIndex = Number(
                MedicalCalculator.calculateBodyMassIndex(
                    weigthInKilograms,
                    this.props.vitalSign.heightInMeters
                )
                    .toFixed(1)
            );
        }
        state.form.Section10.PrePregnancyBodyMassIndex = bodyMassIndex;
    }

    protected getWeightBeforePregnancyDropdownOptions(unit: WeightUnit = WeightUnit.Kilogram): SearchableItemModelT<string>[] {
        return this.props.obstetricalFileMedicalSummary.getThreeLatestVitalSignsWithWeight(this.props.consultationDateAtStartOfObstetricalFile)
            .map(p => {
                const vitalSign = new MedicalItemVitalSign(p);
                const roundedWeight = vitalSign.getWeightIn(unit)?.toFixed(1);
                return new SearchableItemModelT<string>(`${roundedWeight} ${WeightUnit[unit].toLowerCase()} (${new DateOnly(p.PharmaStartDate).toString()})`, roundedWeight);
            });
    }
}

export { BritishColumbiaObstetricalFileSection10 };
